import React, { useEffect, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import resolveImageUrl from "@/lib/imageUrl";
import ImagePicker from "./ImagePicker";

// Human-friendly section labels.
const META = {
  hero:                 "Hero",
  why_become:           "Why become a member?",
  real_people:          "Real people (photo)",
  what_supports:        "What membership supports",
  payment:              "Payment · MobilePay",
  whats_next:           "What happens next",
  what_means:           "What being a member means",
  social_run_showcase:  "Showcase · Social Run",
  values:               "Our values",
  next_chapter:         "Next chapter · KB Hallen",
  volunteer:            "Volunteer · Get involved",
  final_cta:            "Final CTA",
};

const LANGS = [
  { code: "en", label: "English" },
  { code: "da", label: "Dansk" },
  { code: "es", label: "Español" },
];

export default function MembershipAdmin({ authed }) {
  const [sections, setSections] = useState(null);
  const [settings, setSettings] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);  // to detect nav URL mismatch
  const [analysis, setAnalysis] = useState({});
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [publishModal, setPublishModal] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [rSec, rSet, rAna, rSite] = await Promise.all([
      authed.get("/content?kind=membership_page&status="),
      authed.get("/admin/membership/settings"),
      authed.get("/admin/membership/analysis"),
      authed.get("/site-settings"),
    ]);
    const rows = (rSec.data || []).slice().sort((a, b) => ((a.data?.order || 0) - (b.data?.order || 0)));
    setSections(rows);
    setSettings(rSet.data || {});
    setSiteSettings(rSite.data || {});
    const byId = {};
    (rAna.data.sections || []).forEach(s => { byId[s.id] = s; });
    setAnalysis(byId);
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const setState = async (nextState) => {
    const cur = settings?.state || "draft";
    if (nextState === cur) return;
    const prompts = {
      draft: "Take /join OFFLINE? Public visitors will get a 404. No content is deleted.",
      live:  "Publish /join to the public? Anyone with the URL will see the membership page. The Header/Home CTAs will only switch to /join after you click 'Point to /join' in Site Settings.",
    };
    if (!window.confirm(prompts[nextState])) return;
    const r = await authed.put("/admin/membership/settings", { state: nextState });
    setSettings(r.data);
    setMsg(nextState === "live" ? "Membership page is now LIVE." : "Membership page taken OFFLINE.");
    setTimeout(() => setMsg(""), 3500);
  };

  const pointNavToJoin = async () => {
    if (!window.confirm("Update the Header/Home 'Become a member' CTA to /join?")) return;
    const next = { ...(siteSettings || {}), membership: { ...((siteSettings || {}).membership || {}), url: "/join" } };
    await authed.put("/admin/site-settings", next);
    setSiteSettings(next);
    setMsg("Header / Home 'Become a member' now points to /join.");
    setTimeout(() => setMsg(""), 3500);
  };

  const saveSettings = async (patch) => {
    const r = await authed.put("/admin/membership/settings", patch);
    setSettings(r.data);
  };

  const toggleSelected = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openPublishModal = async (scope) => {
    const ids = scope === "selected" ? Array.from(selected) : null;
    const r = await authed.post("/admin/membership/publish-batch", { ids, dry_run: true });
    setPublishModal({ scope, ids, dry: r.data });
  };

  const confirmPublish = async () => {
    const { scope, ids } = publishModal;
    setPublishModal({ ...publishModal, busy: true });
    const r = await authed.post("/admin/membership/publish-batch", { ids: scope === "selected" ? ids : null });
    setPublishModal({ ...publishModal, result: r.data });
    setSelected(new Set());
    await load();
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex(s => s.id === active.id);
    const newIdx = sections.findIndex(s => s.id === over.id);
    const next = arrayMove(sections, oldIdx, newIdx).map((s, i) => ({ ...s, data: { ...s.data, order: i + 1 } }));
    setSections(next);
    await Promise.all(next.map(s =>
      authed.put(`/content/${s.id}`, { data: s.data })
    ));
  };

  if (sections === null || !settings) return <div className="text-ink/50 text-sm">Loading…</div>;

  const state = settings.state || "draft";
  const stateColor = state === "live" ? "bg-emerald-100 text-emerald-800" : "bg-line/30 text-ink/70";
  const stateCopy = state === "live"
    ? "Membership page is PUBLIC. Anyone visiting /join sees the full page."
    : "Membership page is HIDDEN. Public /join returns 404. Only admins with ?preview=1 see it.";

  const navUrl = (siteSettings?.membership?.url) || "/get-involved";
  const showNavMismatch = state === "live" && navUrl !== "/join";

  const publishedCount = sections.filter(s => s.status === "published" && s.data?.visible !== false).length;
  const draftPendingCount = sections.filter(s => !!s.data_draft).length;

  return (
    <div className="space-y-6" data-testid="membership-admin">
      {/* ── Global state card ── */}
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[280px]">
            <div className="label-mono text-terracotta text-[10px]">MEMBERSHIP · /join</div>
            <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">Membership page</h2>
            <div className="mt-3 flex items-center gap-3">
              <span data-testid="membership-state-badge"
                    className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${stateColor}`}>
                {state.toUpperCase()}
              </span>
              <a href="/join?preview=1" target="_blank" rel="noreferrer"
                 data-testid="membership-preview-link"
                 className="text-[11px] underline text-ink/70 hover:text-ink">Open admin preview ↗</a>
            </div>
            <p className="mt-3 text-xs text-ink/60 max-w-xl">{stateCopy}</p>
          </div>
          <div className="flex flex-col items-end gap-2 min-w-[220px]">
            <div className="text-[11px] text-ink/60">
              <span className="text-emerald-700 font-medium">{publishedCount}</span> published
              {" · "}
              <span className="text-amber-700 font-medium">{draftPendingCount}</span> with pending drafts
            </div>
            <div className="flex gap-2">
              <button onClick={() => setState("draft")} disabled={state === "draft"}
                      data-testid="membership-state-draft"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest border ${state === "draft" ? "border-line text-ink/40 bg-line/10 cursor-default" : "border-ink text-ink hover:bg-ink hover:text-white"}`}>
                {state === "live" ? "Take offline" : "Draft"}
              </button>
              <button onClick={() => setState("live")} disabled={state === "live"}
                      data-testid="membership-state-live"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest ${state === "live" ? "bg-emerald-600 text-white cursor-default" : "bg-terracotta text-white hover:bg-terracotta-dark"}`}>
                Set page live
              </button>
            </div>
            {msg && <div className="text-[11px] text-ink/60">{msg}</div>}
          </div>
        </div>

        {showNavMismatch && (
          <div className="mt-5 p-4 border border-amber-300 bg-amber-50 rounded flex flex-wrap items-center gap-3"
               data-testid="membership-nav-mismatch">
            <div className="flex-1 min-w-[240px] text-sm text-amber-900">
              <div className="font-medium">Nav CTA still points to <code className="bg-white/60 px-1.5 py-0.5 rounded text-[12px]">{navUrl}</code></div>
              <div className="text-[12px] mt-0.5">Membership Page is live, but the main "Become a member" CTA is still pointing to {navUrl}.</div>
            </div>
            <button onClick={pointNavToJoin} data-testid="membership-point-to-join"
                    className="px-4 py-2 text-[11px] uppercase tracking-widest bg-amber-800 text-white hover:bg-amber-900">
              Point to /join
            </button>
          </div>
        )}
      </section>

      {/* ── Payment settings ── */}
      <SettingsEditor settings={settings} onSave={saveSettings} />

      {/* ── Bulk publish ── */}
      <section className="border border-line rounded-xl p-4 bg-white flex flex-wrap items-center gap-3">
        <div className="text-[11px] text-ink/60 flex-1 min-w-[220px]">
          <span className="font-medium text-ink">Bulk publish</span> · publishes section content only — does not change page state.
          {selected.size > 0 && <span className="ml-2 text-terracotta">{selected.size} selected</span>}
        </div>
        <button onClick={() => openPublishModal("selected")} disabled={selected.size === 0}
                data-testid="membership-publish-selected"
                className={`px-3 py-2 text-[11px] uppercase tracking-widest border ${selected.size === 0 ? "border-line text-ink/30 cursor-default" : "border-ink text-ink hover:bg-ink hover:text-white"}`}>
          Publish selected
        </button>
        <button onClick={() => openPublishModal("all")}
                data-testid="membership-publish-all"
                className="px-3 py-2 text-[11px] uppercase tracking-widest bg-ink text-white hover:bg-black">
          Publish all sections
        </button>
      </section>

      {/* ── Section list ── */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2" data-testid="membership-sections-list">
            {sections.map((s, idx) => (
              <SortableRow key={s.id} s={s} idx={idx}
                           onEdit={() => setEditing(s)}
                           analysis={analysis[s.id]}
                           checked={selected.has(s.id)}
                           onToggleCheck={() => toggleSelected(s.id)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {editing && (
        <SectionEditor s={editing} authed={authed}
                       onClose={() => { setEditing(null); load(); }} />
      )}

      {publishModal && (
        <PublishBatchModal modal={publishModal} onConfirm={confirmPublish} onClose={() => setPublishModal(null)} />
      )}
    </div>
  );
}

// ─── Payment settings editor ────────────────────────────────────────────
function SettingsEditor({ settings, onSave }) {
  const [form, setForm] = useState(settings);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setForm(settings); }, [settings]);
  const patch = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const patchInstr = (lang, v) => setForm(prev => ({ ...prev, instruction: { ...(prev.instruction || {}), [lang]: v } }));
  const save = async () => {
    await onSave({
      price_dkk: Number(form.price_dkk) || 0,
      currency: form.currency,
      cadence: form.cadence,
      method: form.method,
      method_label: form.method_label,
      identifier: form.identifier,
      instruction: form.instruction,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };
  return (
    <section className="border border-line rounded-xl p-6 bg-white" data-testid="membership-settings">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-mono text-terracotta text-[10px]">SINGLE SOURCE OF TRUTH</div>
          <h3 className="font-editorial text-xl tracking-tight mt-1">Payment settings</h3>
          <p className="text-xs text-ink/60 mt-1">Every payment block on /join reads from here. Change once, updates everywhere.</p>
        </div>
        <button onClick={save} data-testid="membership-settings-save"
                className="px-4 py-2 text-[11px] uppercase tracking-widest bg-terracotta text-white hover:bg-terracotta-dark">
          {saved ? "Saved" : "Save settings"}
        </button>
      </div>
      <div className="mt-5 grid md:grid-cols-4 gap-4">
        <Field label="Price">
          <input type="number" value={form.price_dkk || 0} onChange={e => patch("price_dkk", e.target.value)}
                 data-testid="settings-price" className="border border-line px-3 py-2 w-full text-lg font-editorial" />
        </Field>
        <Field label="Currency">
          <input value={form.currency || ""} onChange={e => patch("currency", e.target.value)}
                 className="border border-line px-3 py-2 w-full" />
        </Field>
        <Field label="Cadence">
          <select value={form.cadence || "year"} onChange={e => patch("cadence", e.target.value)}
                  className="border border-line px-3 py-2 w-full bg-white">
            <option value="year">/ year</option>
            <option value="month">/ month</option>
          </select>
        </Field>
        <Field label="Method label">
          <select value={form.method_label || "MobilePay Box"} onChange={e => patch("method_label", e.target.value)}
                  className="border border-line px-3 py-2 w-full bg-white">
            <option>MobilePay Box</option>
            <option>MobilePay</option>
          </select>
        </Field>
        <Field label="Method">
          <input value={form.method || ""} onChange={e => patch("method", e.target.value)}
                 className="border border-line px-3 py-2 w-full" />
        </Field>
        <Field label="Identifier" span={3}>
          <input value={form.identifier || ""} onChange={e => patch("identifier", e.target.value)}
                 data-testid="settings-identifier"
                 className="border border-line px-3 py-2 w-full font-editorial text-lg tracking-tighter" />
          <div className="text-[11px] text-ink/50 mt-1">e.g. <code>0954SD</code> — displayed as text only; never wrapped in a fake mobilepay:// link.</div>
        </Field>
      </div>
      <div className="mt-4">
        <div className="label-mono text-[11px] text-ink/60 tracking-widest">PAYMENT MESSAGE INSTRUCTION</div>
        <div className="mt-2 grid md:grid-cols-3 gap-3">
          {LANGS.map(l => (
            <Field key={l.code} label={l.label}>
              <textarea value={form.instruction?.[l.code] || ""}
                        onChange={e => patchInstr(l.code, e.target.value)}
                        rows={3} className="border border-line px-3 py-2 w-full text-sm" />
            </Field>
          ))}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, span }) {
  return (
    <label className={`block ${span === 3 ? "md:col-span-3" : ""}`}>
      <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">{label}</div>
      {children}
    </label>
  );
}

// ─── Section row ────────────────────────────────────────────────────────
function SortableRow({ s, idx, onEdit, analysis, checked, onToggleCheck }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const d = s.data || {};
  const thumb = d.images?.[0]?.url;
  const hidden = s.status !== "published" || d.visible === false;
  const hasDraft = !!s.data_draft;
  const name = META[d.slug] || d.slug;
  const a = analysis || {};
  const missingLangs = a.missing_translation || [];
  return (
    <div ref={setNodeRef} style={style} data-testid={`mem-row-${d.slug}`}
         className={`bg-white border ${hidden ? "border-dashed border-line/60" : "border-line"} rounded-lg flex items-center gap-3 p-3`}>
      <input type="checkbox" checked={!!checked} onChange={onToggleCheck}
             data-testid={`mem-check-${d.slug}`}
             className="w-4 h-4 cursor-pointer accent-terracotta" onClick={(e) => e.stopPropagation()} />
      <button {...attributes} {...listeners} className="cursor-grab text-ink/30 hover:text-ink text-xl px-2">≡</button>
      <div className="font-editorial text-lg tabular-nums w-8 text-ink/40">{idx + 1}</div>
      <div className="w-14 h-14 shrink-0 border border-line/50 bg-line/10 overflow-hidden">
        {thumb ? <img src={resolveImageUrl(thumb)} alt="" className="w-full h-full object-cover" />
               : <div className="w-full h-full flex items-center justify-center text-ink/25 text-[10px]">—</div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-editorial text-base truncate">{name}</div>
        <div className="text-[11px] text-ink/50 truncate">{d.template} · {(d.headline?.en || "").slice(0, 60)}</div>
        {(a.missing_image || a.missing_headline || missingLangs.length > 0) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {a.missing_headline && <span className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">NO HEADLINE</span>}
            {a.missing_image && <span className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">MISSING IMAGE</span>}
            {missingLangs.map(l => (
              <span key={l} className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">MISSING {l.toUpperCase()}</span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {hasDraft && <span className="text-[9px] label-mono tracking-widest px-2 py-1 rounded bg-amber-100 text-amber-800">DRAFT</span>}
        <span className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${hidden ? "bg-line/30 text-ink/60" : "bg-emerald-100 text-emerald-800"}`}>
          {hidden ? (s.status === "published" ? "HIDDEN" : "DRAFT") : "PUBLISHED"}
        </span>
        <button onClick={onEdit} data-testid={`mem-edit-${d.slug}`}
                className="border border-ink text-ink px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-ink hover:text-white">Edit</button>
      </div>
    </div>
  );
}

// ─── Section editor (drawer) ────────────────────────────────────────────
function SectionEditor({ s, authed, onClose }) {
  const [data, setData] = useState(s.data_draft || s.data || {});
  const [lang, setLang] = useState("en");
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);

  const patch = (k, v) => setData(prev => ({ ...prev, [k]: v }));
  const patchLang = (k, v) => setData(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [lang]: v } }));
  const patchImage = (idx, patchObj) => {
    const imgs = [...(data.images || [])];
    imgs[idx] = { ...(imgs[idx] || {}), ...patchObj };
    patch("images", imgs);
  };

  const saveDraft = async () => {
    setBusy(true);
    await authed.post(`/content/${s.id}/draft`, { data });
    setBusy(false);
  };
  const publish = async () => {
    setBusy(true);
    await authed.post(`/content/${s.id}/publish`, { data });
    setBusy(false);
    onClose();
  };
  const discardDraft = async () => {
    if (!window.confirm("Discard unsaved draft? This cannot be undone.")) return;
    setBusy(true);
    await authed.post(`/content/${s.id}/discard-draft`);
    setBusy(false);
    onClose();
  };
  const toggleVisible = () => patch("visible", data.visible === false ? true : false);

  const t = data.template;
  const needsImage = ["hero", "full_bleed_image", "stats_showcase", "next_chapter", "final_cta", "volunteer"].includes(t);
  const primaryImage = (data.images || [])[0];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" role="dialog">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-mono text-terracotta text-[10px]">EDIT SECTION</div>
            <div className="font-editorial text-xl">{META[data.slug] || data.slug}</div>
          </div>
          <button onClick={onClose} className="text-2xl text-ink/50 hover:text-ink px-2">×</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Lang tabs */}
          <div className="flex gap-2">
            {LANGS.map(l => (
              <button key={l.code} onClick={() => setLang(l.code)}
                      className={`px-3 py-1 text-[11px] uppercase tracking-widest ${lang === l.code ? "bg-ink text-white" : "border border-line text-ink/60 hover:border-ink hover:text-ink"}`}>
                {l.label}
              </button>
            ))}
          </div>

          {/* Common fields */}
          {t !== "full_bleed_image" && t !== "values" && (
            <Field label={`Eyebrow (${lang})`}>
              <input value={(data.eyebrow || {})[lang] || ""} onChange={e => patchLang("eyebrow", e.target.value)}
                     className="border border-line px-3 py-2 w-full label-mono tracking-widest text-sm" />
            </Field>
          )}
          {t !== "full_bleed_image" && (
            <Field label={`Headline (${lang})`}>
              <textarea value={(data.headline || {})[lang] || ""} onChange={e => patchLang("headline", e.target.value)}
                        rows={2} className="border border-line px-3 py-2 w-full font-editorial text-2xl leading-tight" />
            </Field>
          )}
          {["hero", "editorial_text", "next_chapter", "final_cta", "volunteer"].includes(t) && (
            <Field label={`Body (${lang})`}>
              <textarea value={(data.body || {})[lang] || ""} onChange={e => patchLang("body", e.target.value)}
                        rows={8} className="border border-line px-3 py-2 w-full text-sm leading-relaxed" />
            </Field>
          )}
          {t === "full_bleed_image" && (
            <Field label={`Caption (${lang})`}>
              <textarea value={(data.caption || {})[lang] || ""} onChange={e => patchLang("caption", e.target.value)}
                        rows={3} className="border border-line px-3 py-2 w-full font-editorial text-xl" />
            </Field>
          )}
          {["hero", "stats_showcase", "next_chapter", "final_cta", "volunteer"].includes(t) && (
            <Field label={`CTA label (${lang})`}>
              <input value={(data.cta_label || {})[lang] || ""} onChange={e => patchLang("cta_label", e.target.value)}
                     className="border border-line px-3 py-2 w-full" />
            </Field>
          )}
          {["stats_showcase", "next_chapter"].includes(t) && (
            <Field label="CTA URL">
              <input value={data.cta_url || ""} onChange={e => patch("cta_url", e.target.value)}
                     className="border border-line px-3 py-2 w-full" />
            </Field>
          )}

          {/* Template-specific extras */}
          {t === "payment" && (
            <Field label={`Note after (${lang})`}>
              <textarea value={(data.note_after || {})[lang] || ""} onChange={e => patchLang("note_after", e.target.value)}
                        rows={3} className="border border-line px-3 py-2 w-full text-sm" />
              <div className="text-[11px] text-ink/50 mt-1">Price / MobilePay identifier / instruction are shared — edit them in "Payment settings" above.</div>
            </Field>
          )}
          {t === "final_cta" && (
            <Field label={`Footer line (${lang})`}>
              <input value={(data.footer_line || {})[lang] || ""} onChange={e => patchLang("footer_line", e.target.value)}
                     className="border border-line px-3 py-2 w-full font-editorial text-lg" />
            </Field>
          )}
          {t === "volunteer" && (
            <>
              <Field label={`Form intro (${lang})`}>
                <textarea value={(data.form_intro || {})[lang] || ""} onChange={e => patchLang("form_intro", e.target.value)}
                          rows={3} className="border border-line px-3 py-2 w-full text-sm" />
              </Field>
              <Field label={`Success headline (${lang})`}>
                <input value={(data.success_headline || {})[lang] || ""} onChange={e => patchLang("success_headline", e.target.value)}
                       className="border border-line px-3 py-2 w-full font-editorial text-lg" />
              </Field>
              <Field label={`Success body (${lang})`}>
                <textarea value={(data.success_body || {})[lang] || ""} onChange={e => patchLang("success_body", e.target.value)}
                          rows={3} className="border border-line px-3 py-2 w-full text-sm" />
              </Field>
              <div className="text-[11px] text-ink/50">Form fields (name, email, help-with options, availability) are structural and not translated per-section — labels are localised at render time to keep the form contract stable with the email backend.</div>
            </>
          )}
          {t === "stats_showcase" && (
            <StatsListEditor stats={data.stats || []} lang={lang}
                             onChange={arr => patch("stats", arr)} />
          )}
          {t === "values" && (
            <ValuesListEditor values={data.values || []} lang={lang}
                              onChange={arr => patch("values", arr)} />
          )}

          {/* Image */}
          {needsImage && (
            <div className="border-t border-line pt-6">
              <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-2">IMAGE</div>
              {primaryImage?.url ? (
                <div className="space-y-3">
                  <div className="relative aspect-[16/9] bg-line/10 overflow-hidden">
                    <img src={resolveImageUrl(primaryImage.url)} alt=""
                         className="w-full h-full object-cover"
                         style={{ objectPosition: `${primaryImage.focal?.x ?? 50}% ${primaryImage.focal?.y ?? 50}%` }} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FocalGrid label="Desktop focal" value={primaryImage.focal}
                               onChange={f => patchImage(0, { focal: f })} />
                    <FocalGrid label="Mobile focal" value={primaryImage.focal_mobile}
                               onChange={f => patchImage(0, { focal_mobile: f })} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowPicker(true)}
                            className="border border-ink text-ink px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-ink hover:text-white">Replace image</button>
                    <button onClick={() => patchImage(0, { url: "" })}
                            className="border border-line text-ink/60 px-3 py-1.5 text-[11px] uppercase tracking-widest hover:border-rose-400 hover:text-rose-600">Remove</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowPicker(true)}
                        className="border-2 border-dashed border-line px-6 py-8 w-full text-ink/50 hover:border-ink hover:text-ink">
                  + Select from Media Library
                </button>
              )}
            </div>
          )}

          {/* Visibility */}
          <div className="border-t border-line pt-6 flex items-center justify-between">
            <div>
              <div className="label-mono text-[11px] tracking-widest text-ink/60">VISIBILITY</div>
              <div className="text-sm mt-1">{data.visible === false ? "Hidden from public /join" : "Visible when page is live"}</div>
            </div>
            <button onClick={toggleVisible}
                    className={`px-3 py-2 text-[11px] uppercase tracking-widest ${data.visible === false ? "bg-ink text-white" : "border border-ink text-ink hover:bg-ink hover:text-white"}`}>
              {data.visible === false ? "Show" : "Hide"}
            </button>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-line px-6 py-4 flex flex-wrap gap-2 justify-end">
          {s.data_draft && (
            <button onClick={discardDraft} disabled={busy}
                    className="px-4 py-2 text-[11px] uppercase tracking-widest border border-rose-300 text-rose-600 hover:bg-rose-50">
              Discard draft
            </button>
          )}
          <button onClick={saveDraft} disabled={busy}
                  className="px-4 py-2 text-[11px] uppercase tracking-widest border border-ink text-ink hover:bg-ink hover:text-white">
            Save draft
          </button>
          <button onClick={publish} disabled={busy}
                  className="px-4 py-2 text-[11px] uppercase tracking-widest bg-terracotta text-white hover:bg-terracotta-dark">
            {busy ? "…" : "Publish"}
          </button>
        </div>
      </div>

      {showPicker && (
        <ImagePicker onPick={(asset) => {
          patchImage(0, {
            url: asset.url,
            alt: primaryImage?.alt || { en: asset.alt_text || "", da: asset.alt_text || "", es: asset.alt_text || "" },
          });
          setShowPicker(false);
        }} onClose={() => setShowPicker(false)} authed={authed} />
      )}
    </div>
  );
}

function StatsListEditor({ stats, lang, onChange }) {
  const patch = (i, k, v) => {
    const next = [...stats];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const patchLabel = (i, v) => {
    const next = [...stats];
    next[i] = { ...next[i], label: { ...(next[i].label || {}), [lang]: v } };
    onChange(next);
  };
  return (
    <Field label={`Stats (${lang} label)`}>
      <div className="space-y-2">
        {stats.map((s, i) => (
          <div key={i} className="flex gap-2">
            <input value={s.value || ""} onChange={e => patch(i, "value", e.target.value)}
                   className="border border-line px-3 py-2 w-40 font-editorial" />
            <input value={s.label?.[lang] || ""} onChange={e => patchLabel(i, e.target.value)}
                   className="border border-line px-3 py-2 flex-1" />
          </div>
        ))}
      </div>
    </Field>
  );
}

function ValuesListEditor({ values, lang, onChange }) {
  const patchName = (i, v) => {
    const next = [...values]; next[i] = { ...next[i], name: { ...(next[i].name || {}), [lang]: v } };
    onChange(next);
  };
  const patchText = (i, v) => {
    const next = [...values]; next[i] = { ...next[i], text: { ...(next[i].text || {}), [lang]: v } };
    onChange(next);
  };
  return (
    <Field label={`Values (${lang})`}>
      <div className="space-y-3">
        {values.map((v, i) => (
          <div key={v.key || i} className="border border-line rounded p-3 space-y-2">
            <input value={v.name?.[lang] || ""} onChange={e => patchName(i, e.target.value)}
                   className="border border-line px-3 py-2 w-full label-mono tracking-widest" />
            <textarea value={v.text?.[lang] || ""} onChange={e => patchText(i, e.target.value)}
                      rows={2} className="border border-line px-3 py-2 w-full text-sm" />
          </div>
        ))}
      </div>
    </Field>
  );
}

function FocalGrid({ label, value, onChange }) {
  const points = [
    { x: 0, y: 0 }, { x: 50, y: 0 }, { x: 100, y: 0 },
    { x: 0, y: 50 }, { x: 50, y: 50 }, { x: 100, y: 50 },
    { x: 0, y: 100 }, { x: 50, y: 100 }, { x: 100, y: 100 },
  ];
  const cur = value || { x: 50, y: 50 };
  return (
    <div>
      <div className="label-mono text-[10px] tracking-widest text-ink/60 mb-1">{label}</div>
      <div className="grid grid-cols-3 gap-1 w-fit border border-line p-1">
        {points.map(p => (
          <button key={`${p.x}-${p.y}`} onClick={() => onChange(p)}
                  className={`w-6 h-6 rounded-full ${cur.x === p.x && cur.y === p.y ? "bg-terracotta" : "bg-line/30 hover:bg-ink/40"}`} />
        ))}
      </div>
    </div>
  );
}

// ─── Publish batch modal (mirror of SocialRun) ─────────────────────────
function PublishBatchModal({ modal, onConfirm, onClose }) {
  const { dry, result, busy, scope } = modal;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" data-testid="membership-publish-modal">
      <div className="bg-white max-w-lg w-full rounded-xl border border-line shadow-xl">
        <div className="p-6 border-b border-line">
          <div className="label-mono text-terracotta text-[10px]">MEMBERSHIP · BULK PUBLISH</div>
          <h3 className="font-editorial text-2xl tracking-tighter mt-1">
            {result ? "Done." : (scope === "selected" ? "Publish selected sections" : "Publish all sections")}
          </h3>
          <p className="text-xs text-ink/60 mt-2">
            {result
              ? "Global page state is unchanged. Public /join still respects state=draft."
              : "This publishes section content only. Page state (DRAFT / LIVE) is NOT changed here."}
          </p>
        </div>
        <div className="p-6 space-y-3 text-sm">
          {!result && dry && (
            <>
              <Row label="Sections to publish" value={dry.to_publish} />
              <Row label="Already published (skipped)" value={dry.already_published} muted />
              <Row label="Hidden" value={dry.hidden} warn={dry.hidden > 0} />
              <Row label="Missing headline" value={dry.missing_headline} warn={dry.missing_headline > 0} />
              <Row label="Missing image (template requires one)" value={dry.missing_image} warn={dry.missing_image > 0} />
              <div className="pt-2 border-t border-line/50">
                <div className="text-[11px] text-ink/60 mb-1">Translation gaps (not blocking)</div>
                <div className="grid grid-cols-3 gap-2">
                  <MiniRow lang="EN" n={dry.missing_translation_by_lang.en} />
                  <MiniRow lang="DA" n={dry.missing_translation_by_lang.da} />
                  <MiniRow lang="ES" n={dry.missing_translation_by_lang.es} />
                </div>
              </div>
            </>
          )}
          {result && (
            <>
              <Row label="Sections published" value={result.published_count} />
              <Row label="Hidden after publish" value={result.hidden} muted />
              <Row label="Sections with missing headline" value={result.missing_headline} muted />
              <Row label="Sections with missing image" value={result.missing_image} muted />
            </>
          )}
        </div>
        <div className="p-4 border-t border-line flex justify-end gap-2">
          {!result ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-widest border border-ink text-ink hover:bg-ink hover:text-white">Cancel</button>
              <button onClick={onConfirm} disabled={busy || (dry?.to_publish || 0) === 0}
                      data-testid="membership-modal-confirm"
                      className={`px-4 py-2 text-[11px] uppercase tracking-widest ${(dry?.to_publish || 0) === 0 ? "bg-line text-ink/40 cursor-default" : "bg-terracotta text-white hover:bg-terracotta-dark"}`}>
                {busy ? "Publishing…" : `Confirm publish (${dry?.to_publish || 0})`}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-widest bg-ink text-white hover:bg-black">Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, muted, warn }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-ink/50" : "text-ink/80"}>{label}</span>
      <span className={`font-editorial text-lg tabular-nums ${warn ? "text-rose-700" : muted ? "text-ink/40" : "text-ink"}`}>{value}</span>
    </div>
  );
}
function MiniRow({ lang, n }) {
  return (
    <div className={`flex flex-col items-center py-2 rounded ${n > 0 ? "bg-amber-50 text-amber-800" : "bg-line/10 text-ink/40"}`}>
      <span className="font-editorial text-xl tabular-nums">{n}</span>
      <span className="text-[10px] label-mono tracking-widest">{lang}</span>
    </div>
  );
}
